import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { calculateResult } from '../../data/quizData';

const { FiChevronDown, FiAlertCircle, FiLock, FiLoader, FiArrowRight } = FiIcons;

const LeadForm = ({ onSubmit, answers }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zip: '',
    timeToCall: 'Morning 8-12',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'phone') newValue = formatPhone(value);
    if (name === 'zip') newValue = value.replace(/\D/g, '').slice(0, 5);
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email is required';
    if (formData.phone.length < 14) newErrors.phone = 'Complete phone number required';
    if (formData.zip.length < 5) newErrors.zip = 'Valid 5-digit ZIP required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const resultType = calculateResult(answers);
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        zip: formData.zip,
        address_state: 'CA',
        source: 'Web',
        quiz_result: resultType || null,
        time_to_call: formData.timeToCall || null,
        notes: 'auto_insurance_quiz',
      };

      console.log('📤 Supabase insert payload:', payload);
      const { data, error, status, statusText } = await supabase
        .from('clients')
        .insert([payload]);
      console.log('📥 Supabase response:', { data, error, status, statusText });

      if (error) {
        console.error('❌ Supabase insert error:', JSON.stringify(error, null, 2));
        if (error.code === '23505') {
          setSubmitError('This email is already registered. Please use a different email address.');
        } else {
          setSubmitError('Something went wrong. Please try again.');
        }
        setIsSubmitting(false);
        return;
      }

      console.log('✅ Supabase insert successful');
      onSubmit(formData, resultType);
    } catch (err) {
      console.error('❌ Unexpected error:', err.message);
      setSubmitError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-10 border border-gray-100"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Get Your Personalized Coverage Analysis
        </h2>
        <p className="text-gray-600">
          Based on your answers, we found some important insights about your coverage.
          Enter your info below to see your results.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name*</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'} focus:ring-2 focus:ring-blue-200 outline-none transition-colors`}
              placeholder="John"
            />
            {errors.firstName && <span className="text-xs text-red-500 mt-1">{errors.firstName}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name*</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'} focus:ring-2 focus:ring-blue-200 outline-none transition-colors`}
              placeholder="Doe"
            />
            {errors.lastName && <span className="text-xs text-red-500 mt-1">{errors.lastName}</span>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address*</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'} focus:ring-2 focus:ring-blue-200 outline-none transition-colors`}
            placeholder="john@example.com"
          />
          {errors.email && <span className="text-xs text-red-500 mt-1">{errors.email}</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number*</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'} focus:ring-2 focus:ring-blue-200 outline-none transition-colors`}
            />
            {errors.phone && <span className="text-xs text-red-500 mt-1">{errors.phone}</span>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code*</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              placeholder="90210"
              className={`w-full px-4 py-3 rounded-lg border ${errors.zip ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'} focus:ring-2 focus:ring-blue-200 outline-none transition-colors`}
            />
            {errors.zip && <span className="text-xs text-red-500 mt-1">{errors.zip}</span>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Best time to call</label>
          <div className="relative">
            <select
              name="timeToCall"
              value={formData.timeToCall}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none appearance-none bg-white"
            >
              <option value="Morning 8-12">Morning (8am - 12pm)</option>
              <option value="Afternoon 12-5">Afternoon (12pm - 5pm)</option>
              <option value="Evening 5-8">Evening (5pm - 8pm)</option>
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {submitError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <SafeIcon icon={FiAlertCircle} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex items-start gap-3 mt-6 mb-6">
          <SafeIcon icon={FiLock} className="text-gray-400 mt-1 flex-shrink-0" />
          <p className="text-xs text-gray-500 leading-relaxed">
            Your information is secure and will never be shared. We'll use it only to send
            your results and schedule your free coverage review.
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <SafeIcon icon={FiLoader} className="animate-spin" />
              <span>Analyzing Results...</span>
            </>
          ) : (
            <>
              <span>Show Me My Results</span>
              <SafeIcon icon={FiArrowRight} />
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default LeadForm;